import { Directive, ElementRef, HostListener, Input, Output, EventEmitter, Renderer2, OnDestroy, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appMention]',
})
export class MentionDirective implements OnDestroy {
  @Input() mentionList: any[] = [];
  @Input() mentionIdsControl!: string; // 🔥 Generic ID control
  @Output() mentionSelected = new EventEmitter<any>();

  private dropdown!: HTMLElement;
  private filteredList: any[] = [];
  private activeIndex = 0;
  private isOpen = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Optional() @Self() private ngControl: NgControl
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = {
        writeValue: (value: any) => {
          this.el.nativeElement.innerHTML = value || '';
        },
        registerOnChange: (fn: any) => {
          this.onChange = fn;
        },
        registerOnTouched: () => {},
        setDisabledState: () => {},
      } as any;
    }
  }

  private onChange = (_: any) => {};

  // -----------------------------
  // INPUT
  // -----------------------------
  @HostListener('input')
  onInput() {
    const value = this.el.nativeElement.innerHTML;
    this.onChange(value);
    this.updateMentionIds();
  }

  // -----------------------------
  // DETECT @
  // -----------------------------
  @HostListener('keyup', ['$event'])
  onKeyUp(event: KeyboardEvent) {
    if (['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(event.key)) {
      return;
    }

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();

    preCaretRange.selectNodeContents(this.el.nativeElement);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    const textBeforeCaret = preCaretRange.toString();
    const match = textBeforeCaret.match(/@(\w*)$/);

    if (!match) {
      this.closeDropdown();
      return;
    }

    const search = match[1].toLowerCase();

    this.filteredList = this.mentionList.filter(agent => `${agent.name || ''}`.toLowerCase().includes(search));

    this.activeIndex = 0;

    if (this.filteredList.length) {
      this.openDropdown();
    } else {
      this.closeDropdown();
    }
  }

  // -----------------------------
  // KEYBOARD NAVIGATION
  // -----------------------------
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (!this.isOpen) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex = (this.activeIndex + 1) % this.filteredList.length;
      this.updateActiveItem();
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex = (this.activeIndex - 1 + this.filteredList.length) % this.filteredList.length;
      this.updateActiveItem();
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const agent = this.filteredList[this.activeIndex];
      if (agent) this.selectAgent(agent);
    }

    if (event.key === 'Escape') {
      this.closeDropdown();
    }
  }

  // -----------------------------
  // INSERT TAG
  // -----------------------------
  private selectAgent(agent: any) {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);

    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(this.el.nativeElement);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    const textBeforeCaret = preCaretRange.toString();
    const match = textBeforeCaret.match(/@(\w*)$/);

    if (match) {
      range.setStart(range.endContainer, range.endOffset - match[0].length);
      range.deleteContents();
    }

    const tag = this.renderer.createElement('span');
    this.renderer.addClass(tag, 'mention-tag');
    tag.contentEditable = 'false';
    tag.innerText = `@${agent.name || ''}`;
    tag.setAttribute('data-id', agent._id || '');

    range.insertNode(tag);

    const space = document.createTextNode(' ');
    tag.after(space);

    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);

    selection.removeAllRanges();
    selection.addRange(newRange);

    this.onChange(this.el.nativeElement.innerHTML);
    this.updateMentionIds();

    this.mentionSelected.emit(agent);
    this.closeDropdown();
  }

  // -----------------------------
  // UPDATE IDS (GENERIC)
  // -----------------------------
  private updateMentionIds() {
    if (!this.mentionIdsControl) return;
    if (!this.ngControl?.control?.parent) return;

    const tags = this.el.nativeElement.querySelectorAll('.mention-tag');
    const ids: string[] = [];

    tags.forEach((tag: HTMLElement) => {
      const id = tag.getAttribute('data-id');
      if (id && !ids.includes(id)) {
        ids.push(id);
      }
    });

    this.ngControl.control.parent.get(this.mentionIdsControl)?.setValue(ids, { emitEvent: false });
  }

  // -----------------------------
  // DROPDOWN
  // -----------------------------
  private openDropdown() {
    this.closeDropdown();

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(true);

    const rect = range.getBoundingClientRect();
    const parentRect = this.el.nativeElement.parentElement.getBoundingClientRect();

    this.dropdown = this.renderer.createElement('ul');

    this.renderer.setStyle(this.dropdown, 'position', 'absolute');
    this.renderer.setStyle(this.dropdown, 'top', `${rect.bottom - parentRect.top}px`);
    this.renderer.setStyle(this.dropdown, 'left', `${rect.left - parentRect.left}px`);
    this.renderer.setStyle(this.dropdown, 'background', '#fff');
    this.renderer.setStyle(this.dropdown, 'border', '1px solid #e8eaec');
    this.renderer.setStyle(this.dropdown, 'border-radius', '12px');
    this.renderer.setStyle(this.dropdown, 'width', '250px');
    this.renderer.setStyle(this.dropdown, 'max-height', '200px');
    this.renderer.setStyle(this.dropdown, 'overflow', 'auto');
    this.renderer.setStyle(this.dropdown, 'z-index', '1000');
    this.renderer.setStyle(this.dropdown, 'list-style', 'none');
    this.renderer.setStyle(this.dropdown, 'padding', '7px');
    this.renderer.setStyle(this.dropdown, 'margin', '4px 0 0 0');

    this.filteredList.forEach((agent, index) => {
      const item = this.renderer.createElement('li');
      item.innerText = `@${agent.name || ''}`;
      this.renderer.setStyle(item, 'padding', '8px');
      this.renderer.setStyle(item, 'cursor', 'pointer');

      if (index === this.activeIndex) {
        this.renderer.setStyle(item, 'background', '#e9f4f1');
      }

      this.renderer.listen(item, 'mousedown', (e: Event) => {
        e.preventDefault();
        this.selectAgent(agent);
      });

      this.renderer.appendChild(this.dropdown, item);
    });

    this.el.nativeElement.parentElement.appendChild(this.dropdown);
    this.isOpen = true;
  }

  private updateActiveItem() {
    const items = this.dropdown?.children;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      this.renderer.setStyle(items[i], 'background', i === this.activeIndex ? '#e9f4f1' : '#fff');
    }
  }

  private closeDropdown() {
    if (this.dropdown) this.dropdown.remove();
    this.isOpen = false;
  }

  ngOnDestroy() {
    this.closeDropdown();
  }
}
